import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType, concatLatestFrom } from '@ngrx/effects';
import { catchError, first, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { ConversationDataService } from '../../core/services/conversation-data.service';
import { ScorecardService } from '../../core/services/scorecard.service';
import { PDF_EXPORT_ACTIONS } from '../actions/pdf-export.actions';
import { APPLICATION_REDUCER } from '../reducers/application.reducer';
import { TRANSCRIPTION_REDUCER } from '../reducers/transcription.reducer';
import { CONVERSATION_DATA_REDUCER } from '../reducers/conversation-data.reducer';
import { SCORECARD_REDUCER } from '../reducers/scorecard.reducer';
import { IConversationDetails } from '../../shared/models/conversation-details.model';
import { IStandardTranscriptNormalized } from '../../shared/models/standard-transcript-normalized.model';
import {
  IPdfExportData,
  IPdfScorecard,
  PdfExportService,
  PdfScorecardModel,
} from '../../core/services/pdf-export.service';
import { EPdfContentType } from '../../components/transcriptions/components/pdf-content-selection-dialog/pdf-content-selection-dialog.component';
import { IDataQueryParam } from '../actions';
import { IContactDetails } from '../../shared/models/contact-details.model';
import { IAppointmentDetails } from '../../shared/models/appointment-details.model';
import { ILeasingPreferences } from '../../shared/models/leasing-preferences.model';
import { ICampaignDetails } from '../../shared/models/campaign-details.model';

@Injectable()
export class PdfExportEffects {
  constructor(
    private readonly conversationDataService: ConversationDataService,
    private readonly scorecardService: ScorecardService,
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly pdfExportService: PdfExportService,
  ) {}

  exportPdf$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(PDF_EXPORT_ACTIONS.export),
        concatLatestFrom(() => this.store.select(APPLICATION_REDUCER.selectDataQueryParam)),
        switchMap(([{ selectedContent }, queryParam]) => this.getDataForPDF(selectedContent, queryParam)),
        tap((data) => void this.pdfExportService.generatePdf(data)),
      );
    },
    { dispatch: false },
  );

  private getDataForPDF(selectedContent: EPdfContentType[], queryParam: IDataQueryParam): Observable<IPdfExportData> {
    return forkJoin({
      userName: this.getUserName(selectedContent),
      transcription: this.getTranscription(selectedContent),
      contactDetails: this.getContactDetails(selectedContent, queryParam),
      appointmentDetails: this.getAppointmentDetails(selectedContent, queryParam),
      leasingPreferences: this.getLeasingPreferences(selectedContent, queryParam),
      campaignDetails: this.getCampaignDetails(selectedContent, queryParam),
      conversationDetails: this.getConversationDetails(selectedContent, queryParam),
      scorecards: this.getScorecards(selectedContent, queryParam),
      startDate: this.store.select(TRANSCRIPTION_REDUCER.startDate).pipe(first()),
    });
  }

  private getUserName(selectedContent: EPdfContentType[]): Observable<string | null> {
    if (selectedContent.includes(EPdfContentType.transcript)) {
      return this.store.select(CONVERSATION_DATA_REDUCER.userName).pipe(first());
    }
    return of(null);
  }

  private getTranscription(selectedContent: EPdfContentType[]): Observable<IStandardTranscriptNormalized | null> {
    if (selectedContent.includes(EPdfContentType.transcript)) {
      return this.store.select(TRANSCRIPTION_REDUCER.selectStandardTranscription).pipe(
        map(({ data }) => data),
        first(),
      );
    }
    return of(null);
  }

  private getContactDetails(
    selectedContent: EPdfContentType[],
    { value, queryName }: IDataQueryParam,
  ): Observable<IContactDetails | null> {
    if (selectedContent.includes(EPdfContentType.details)) {
      return this.store.select(CONVERSATION_DATA_REDUCER.selectContactDetails).pipe(
        first(),
        switchMap(({ data }) =>
          data
            ? of(data)
            : this.conversationDataService.getContactDetails(queryName, value).pipe(catchError(() => of(null))),
        ),
      );
    }
    return of(null);
  }

  private getAppointmentDetails(
    selectedContent: EPdfContentType[],
    { value, queryName }: IDataQueryParam,
  ): Observable<IAppointmentDetails | null> {
    if (selectedContent.includes(EPdfContentType.details)) {
      return this.store.select(CONVERSATION_DATA_REDUCER.selectAppointmentDetails).pipe(
        first(),
        switchMap(({ data }) =>
          data
            ? of(data)
            : this.conversationDataService.getAppointmentDetails(queryName, value).pipe(catchError(() => of(null))),
        ),
      );
    }
    return of(null);
  }

  private getLeasingPreferences(
    selectedContent: EPdfContentType[],
    { value, queryName }: IDataQueryParam,
  ): Observable<ILeasingPreferences | null> {
    if (selectedContent.includes(EPdfContentType.details)) {
      return this.store.select(CONVERSATION_DATA_REDUCER.selectLeasingPreferences).pipe(
        first(),
        switchMap(({ data }) =>
          data
            ? of(data)
            : this.conversationDataService.getLeasingPreferences(queryName, value).pipe(catchError(() => of(null))),
        ),
      );
    }
    return of(null);
  }

  private getCampaignDetails(
    selectedContent: EPdfContentType[],
    { value, queryName }: IDataQueryParam,
  ): Observable<ICampaignDetails | null> {
    if (selectedContent.includes(EPdfContentType.details)) {
      return this.store.select(CONVERSATION_DATA_REDUCER.selectCampaignDetails).pipe(
        first(),
        switchMap(({ data }) =>
          data
            ? of(data)
            : this.conversationDataService.getCampaignDetails(queryName, value).pipe(catchError(() => of(null))),
        ),
      );
    }
    return of(null);
  }

  private getConversationDetails(
    selectedContent: EPdfContentType[],
    { value, queryName }: IDataQueryParam,
  ): Observable<IConversationDetails | null> {
    if (selectedContent.includes(EPdfContentType.details)) {
      return this.store.select(CONVERSATION_DATA_REDUCER.selectConversationDetails).pipe(
        first(),
        switchMap(({ data }) =>
          data
            ? of(data)
            : this.conversationDataService.getConversationDetails(queryName, value).pipe(catchError(() => of(null))),
        ),
      );
    }
    return of(null);
  }

  private getScorecards(
    selectedContent: EPdfContentType[],
    { value, queryName }: IDataQueryParam,
  ): Observable<IPdfScorecard[] | null> {
    if (selectedContent.includes(EPdfContentType.scorecards)) {
      return this.store.select(SCORECARD_REDUCER.selectScorecardPreview).pipe(
        first(),
        switchMap(({ data: scorecardPreviews }) => {
          if (!scorecardPreviews?.length) return of(null);

          const scorecardRequests = scorecardPreviews.map((preview) =>
            this.scorecardService
              .getScorecardDetails(queryName, value, preview)
              .pipe(map((scorecard) => new PdfScorecardModel({ ...scorecard, ...preview }))),
          );

          return forkJoin(scorecardRequests);
        }),
      );
    }
    return of(null);
  }
}
